"use client";

import { motion, useTransform } from "framer-motion";
import type { MotionValue } from "framer-motion";

import { cn } from "@/lib/utils";

/**
 * Ruler / scrubber progress element (Figma 8883-24994 desktop, 8883-25117 mobile).
 * Uniform rounded ticks in exactly two colors: played ticks are grey-500, upcoming
 * ticks grey-100. The played region is the same tick row clipped to `progress` (0→1).
 */

const DESKTOP_TICK_COUNT = 36;
const MOBILE_TICK_COUNT = 45;

interface WaveformProps {
  progress: MotionValue<number>;
  className?: string;
  /** Tick density — Figma mobile (8883-25117) uses 45; desktop (8883-24994) uses 36. */
  tickCount?: number;
  /** When set with `onStepSelect`, clicks map the track into equal step segments. */
  stepCount?: number;
  onStepSelect?: (stepIndex: number) => void;
}

export function Waveform({
  progress,
  className,
  tickCount = DESKTOP_TICK_COUNT,
  stepCount,
  onStepSelect,
}: WaveformProps) {
  const clipPath = useTransform(
    progress,
    (p) => `inset(0 ${(1 - Math.min(1, Math.max(0, p))) * 100}% 0 0)`
  );
  const interactive = Boolean(stepCount && stepCount > 0 && onStepSelect);

  const handleTrackClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (!interactive || !stepCount || !onStepSelect) return;

    const rect = event.currentTarget.getBoundingClientRect();
    const x = (event.clientX - rect.left) / rect.width;
    const stepIndex = Math.min(
      stepCount - 1,
      Math.max(0, Math.floor(x * stepCount))
    );
    onStepSelect(stepIndex);
  };

  const ticks = Array.from({ length: tickCount }, (_, i) => i);

  const tickRows = (
    <>
      <TickRow ticks={ticks} className="bg-grey-100" />
      <motion.div className="absolute inset-0" style={{ clipPath }}>
        <TickRow ticks={ticks} className="bg-grey-500" />
      </motion.div>
    </>
  );

  return (
    <div
      className={cn(
        "flex h-9 items-center rounded-[8px] bg-white px-[10px] shadow-[0px_4.827px_24.134px_0px_rgba(0,0,0,0.06)]",
        className
      )}
    >
      {interactive ? (
        <button
          type="button"
          onClick={handleTrackClick}
          aria-label="Underwriting steps"
          className="relative h-4 w-full cursor-pointer border-0 bg-transparent p-0"
        >
          {tickRows}
        </button>
      ) : (
        <div aria-hidden="true" className="relative h-4 w-full">
          {tickRows}
        </div>
      )}
    </div>
  );
}

function TickRow({ ticks, className }: { ticks: number[]; className: string }) {
  return (
    <div className="absolute inset-0 flex justify-between">
      {ticks.map((i) => (
        <span
          key={i}
          className={cn(
            "w-[1.5px] shrink-0 self-stretch rounded-full",
            className
          )}
        />
      ))}
    </div>
  );
}

export { DESKTOP_TICK_COUNT, MOBILE_TICK_COUNT };
